import type { SyncedThread, SyncedWorkspace } from '@/lib/desktop-shell/store';

/**
 * Resolves the physical checkout usable by this device for a persisted thread.
 * Local-first conversations are device-owned. A checkout with the same remote
 * identity on another device is not an implicit execution target; moving the
 * conversation requires an explicit handoff.
 */
export function workspaceReplicaForThread(
  thread: Pick<SyncedThread, 'workspaceId'>,
  workspaces: readonly SyncedWorkspace[],
): SyncedWorkspace | undefined {
  const source = workspaces.find((candidate) => candidate.id === thread.workspaceId);
  if (source === undefined) return undefined;
  return source.availableOnDevice === false ? undefined : source;
}

/** Metadata survives even when no physical replica exists on this device. */
export function sourceWorkspaceForThread(
  thread: Pick<SyncedThread, 'workspaceId'>,
  workspaces: readonly SyncedWorkspace[],
): SyncedWorkspace | undefined {
  return workspaces.find((candidate) => candidate.id === thread.workspaceId);
}

/** Resolves this machine's explicit workspace association for a logical workspace. */
export function localWorkspaceAssociation(
  sourceRoot: string | null,
  workspaces: readonly SyncedWorkspace[],
): SyncedWorkspace | undefined {
  if (sourceRoot === null) return undefined;
  const source = workspaces.find((candidate) => candidate.rootPath === sourceRoot);
  if (source?.logicalWorkspaceId === undefined) return undefined;
  return workspaces.find(
    (candidate) =>
      candidate.logicalWorkspaceId === source.logicalWorkspaceId &&
      candidate.registered === true &&
      (candidate.runtime === 'local' || candidate.runtime === 'worktree') &&
      candidate.availableOnDevice !== false,
  );
}
